import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { TRUE_COLORS } from '../theme';
import { EmptyState, currency, number } from './ui';

export type ProfitRow = {
  product: number | null; name: string; quantity: number; sales: number; cost: number;
  profit: number | null; margin: number | null; missing_cost: boolean;
};
/** api/receipts/profit/: what the period's sales brought, what was sold cost and the bonus saldo spent on them. */
export type Profit = {
  sales: number; cost: number; bonus: number; profit: number; margin: number | null;
  missing: { count: number; sales: number; products: { product: number | null; name: string }[] };
  products: ProfitRow[];
};

export const percent = (value: number | null | undefined) => (value == null ? '—' : `${(value * 100).toFixed(1)}%`);

const productLink = (product: number | null) => (product ? `/admin/products/${product}` : '/admin/products');

/** The products sold without a cost: their sales count but subtract nothing, so the profit is higher than the real one. */
export const MissingCosts = ({ missing }: { missing: Profit['missing'] }) => {
  if (!missing.count) return null;
  const names = missing.products.slice(0, 3);
  return (
    <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }} data-testid="profit-missing">
      {missing.count === 1 ? '1 producto vendido no tiene costo' : `${number(missing.count)} productos vendidos no tienen costo`}
      {' '}({currency(missing.sales)} en ventas): {names.map((p, i) => (
        <React.Fragment key={`${p.product}-${p.name}`}>
          {i > 0 && ', '}
          <Link component={RouterLink} to={productLink(p.product)}>{p.name}</Link>
        </React.Fragment>
      ))}{missing.count > names.length ? '…' : ''}. Tu utilidad real es menor: agrega su costo en Productos.
    </Alert>
  );
};

/** Sales − what was sold cost − bonus saldo spent = gross profit, with its margin. */
export const ProfitSummary = ({ profit }: { profit: Profit }) => (
  <Stack spacing={1.25} data-testid="profit-summary">
    {[
      { label: 'Ventas', value: profit.sales, sign: 1 },
      { label: 'Costo de lo vendido', value: profit.cost, sign: -1 },
      ...(profit.bonus > 0 ? [{ label: 'Saldo de regalo usado', value: profit.bonus, sign: -1 }] : []),
    ].map(row => (
      <Stack key={row.label} direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{row.label}</Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: row.sign < 0 && row.value > 0 ? TRUE_COLORS.danger : 'text.primary' }}>
          {row.sign < 0 && row.value > 0 ? '−' : ''}{currency(row.value)}
        </Typography>
      </Stack>
    ))}
    <Box sx={{ borderTop: `1px solid ${TRUE_COLORS.borderSolid}`, pt: 1.5, mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography sx={{ fontWeight: 700 }}>Utilidad bruta</Typography>
      <Box sx={{ textAlign: 'right' }}>
        <Typography data-testid="profit-total" sx={{ fontWeight: 700, fontSize: '1.125rem', color: profit.profit < 0 ? TRUE_COLORS.danger : TRUE_COLORS.success }}>
          {currency(profit.profit)}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Margen {percent(profit.margin)}</Typography>
      </Box>
    </Box>
    <MissingCosts missing={profit.missing} />
  </Stack>
);

/** Each product's sales, cost and profit, the ones that earn the most first. */
export const ProfitByProduct = ({ rows, limit = 12 }: { rows: ProfitRow[]; limit?: number }) => {
  const [all, setAll] = React.useState(false);
  if (rows.length === 0) return <EmptyState compact title="Sin productos vendidos" />;
  const shown = all ? rows : rows.slice(0, limit);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" data-testid="profit-products">
        <TableHead>
          <TableRow>
            <TableCell>Producto</TableCell>
            <TableCell align="right">Vendidos</TableCell>
            <TableCell align="right">Ventas</TableCell>
            <TableCell align="right">Costo</TableCell>
            <TableCell align="right">Utilidad</TableCell>
            <TableCell align="right">Margen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map(r => (
            <TableRow key={`${r.product}-${r.name}`} hover>
              <TableCell sx={{ fontWeight: 500, maxWidth: 260 }}>
                <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{r.name}</Typography>
              </TableCell>
              <TableCell align="right">{number(r.quantity, 2)}</TableCell>
              <TableCell align="right">{currency(r.sales)}</TableCell>
              <TableCell align="right">
                {r.missing_cost ? (
                  <Chip size="small" label="Sin costo" component={RouterLink} to={productLink(r.product)} clickable
                    sx={{ bgcolor: 'rgba(183,121,31,0.12)', color: TRUE_COLORS.warning, fontWeight: 600 }} />
                ) : currency(r.cost)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: r.profit != null && r.profit < 0 ? TRUE_COLORS.danger : 'text.primary' }}>
                {r.profit == null ? '—' : currency(r.profit)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>{percent(r.margin)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > limit && (
        <Box sx={{ px: 2, py: 1 }}>
          <Link component="button" variant="body2" onClick={() => setAll(v => !v)}>
            {all ? 'Ver menos' : `Ver los ${number(rows.length)} productos`}
          </Link>
        </Box>
      )}
    </Box>
  );
};
